import { useMemo } from 'react';
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

interface ChartSpec {
    type: 'bar' | 'line' | 'pie';
    data: Record<string, unknown>[];
    xKey?: string;
    yKey?: string;
    keys?: string[];
    title?: string;
    colors?: string[];
}

const DEFAULT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

interface InlineChartProps {
    spec: string;
}

export function InlineChart({ spec }: InlineChartProps) {
    const parsed = useMemo<ChartSpec | null>(() => {
        try {
            return JSON.parse(spec);
        } catch {
            return null;
        }
    }, [spec]);

    if (!parsed || !parsed.data) {
        return (
            <div className="my-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
                Invalid chart specification
            </div>
        );
    }

    const colors = parsed.colors ?? DEFAULT_COLORS;
    const { type, data, xKey, yKey, keys, title } = parsed;

    return (
        <div className="my-3 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
            {title && (
                <h4 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">{title}</h4>
            )}
            <ResponsiveContainer width="100%" height={250}>
                {type === 'bar' ? (
                    <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={xKey || 'name'} tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend />
                        {(keys || [yKey || 'value']).map((key, i) => (
                            <Bar key={key} dataKey={key!} fill={colors[i % colors.length]} />
                        ))}
                    </BarChart>
                ) : type === 'line' ? (
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={xKey || 'name'} tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend />
                        {(keys || [yKey || 'value']).map((key, i) => (
                            <Line key={key} type="monotone" dataKey={key!} stroke={colors[i % colors.length]} strokeWidth={2} />
                        ))}
                    </LineChart>
                ) : (
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey={yKey || 'value'}
                            nameKey={xKey || 'name'}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label
                        >
                            {data.map((_, i) => (
                                <Cell key={i} fill={colors[i % colors.length]} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                )}
            </ResponsiveContainer>
        </div>
    );
}
